import { call, put, take, takeLatest } from 'redux-saga/effects';
import { eventChannel, END, buffers } from 'redux-saga';
import axios from 'axios';
import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { extractErrorMessage, extractErrorCode } from '../../../utils/apiResponseHandler';
import { Error } from '../../actions/error/error';
import {
  REQUEST, fetchSuccess, fetchFailure, uploadProgress,
} from '../../actions/foundation/byomUpload';

// Streams axios upload progress events into the saga so the UI can
// render a real progress bar for large model files.
function createUploadChannel(file) {
  // Expanding buffer is CRITICAL: the axios request starts the moment the
  // channel is created, so progress/response events can fire before the
  // saga's first take(). The default buffers.none() silently DROPS those
  // events - on a fast upload the response itself would be lost and the
  // wizard would hang at 0% forever.
  return eventChannel((emit) => {
    const form = new FormData();
    form.append('file', file);

    axios.post(`${config.API_URL}/models/byom/upload`, form, {
      headers: { ...getLocalUser(), 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (evt.total) {
          emit({ progress: Math.round((evt.loaded / evt.total) * 100) });
        }
      },
    })
      .then((response) => { emit({ response }); emit(END); })
      .catch((error) => { emit({ error }); emit(END); });

    return () => {};
  }, buffers.expanding(16));
}

function* process(action) {
  const { file } = action.payload || {};
  if (!file) {
    yield put(fetchFailure({ message: 'No file selected' }));
    return;
  }
  const channel = yield call(createUploadChannel, file);
  try {
    while (true) {
      // take(channel) is the only correct way to consume an eventChannel
      // from a saga; when the channel emits END the take terminates the
      // loop via the finally block.
      const event = yield take(channel);
      if (event.progress !== undefined) {
        yield put(uploadProgress(event.progress));
      } else if (event.response) {
        yield put(fetchSuccess(event.response.data?.data || event.response.data));
        break;
      } else if (event.error) {
        yield put(Error(event.error));
        yield put(fetchFailure({
          message: extractErrorMessage(event.error),
          code: extractErrorCode(event.error),
          details: event.error?.response?.data?.error?.details,
        }));
        break;
      }
    }
  } finally {
    channel.close();
  }
}

export function* watch() {
  yield takeLatest(REQUEST, process);
}
